import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { FocusTrap } from "focus-trap-react";
import React from "react";
import ReactDOM from "react-dom";
import { FaTimes } from "react-icons/fa";

import { bp } from "../../constants";

import Background from "./Background";
import Button from "./Button";
import ScrollFadeOverlay from "./ScrollFadeOverlay";
import SpaceBetweenDiv from "./SpaceBetweenDiv";

const wrapper = css`
  position: fixed;
  pointer-events: none;
  z-index: 1002;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
`;

const ChildrenWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background-color: var(--mi-background-color);
  border-radius: var(--mi-border-radius-x) var(--mi-border-radius-x) 0 0;
  padding-top: var(--mi-border-radius-x);
`;

const ScrollArea = styled.div<{
  noPadding?: boolean;
}>`
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;

  ${(props) => (props.noPadding ? "padding: 0;" : "padding: 0 20px 20px 20px;")}

  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-track {
    background-color: inset 0 0 0px rgba(0, 0, 0);
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: grey;
  }

  @media (max-width: ${bp.small}px) {
    ${(props) => (props.noPadding ? "" : "padding: 0 1rem 1rem 1rem;")}
  }
`;

type ContentProps = {
  size?: "small" | "medium";
  contentTitle?: string | null;
};

const Content = styled.div<ContentProps>`
  pointer-events: auto;
  overflow: hidden;
  position: absolute;
  left: 0;
  right: 0;
  margin: 0 auto;
  max-height: calc(100vh - 150px);
  padding-top: 0;
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.contentTitle
      ? "background-color: var(--mi-background-color); border: 1px solid var(--mi-darken-background-color);"
      : ""}

  ${(props) =>
    props.size === "small"
      ? "width: 30%;"
      : props.size === "medium"
        ? "width: 500px;"
        : "width: 80%; max-width: var(--mi-container-medium);"};

  border-radius: var(--mi-border-radius-x);

  ${(props) =>
    props.size === "small" ? "" : "max-height: calc(100vh - 70px);"}

  animation: 300ms ease-out forwards slide-up;

  h1 {
    display: inline-block;
  }

  @media (max-width: ${bp.xlarge}px) {
    ${(props) => (props.size === "small" ? "width: 50%;" : "")}
  }

  @media (max-width: ${bp.large}px) {
    ${(props) => (props.size === "small" ? "width: 70%;" : "")}
  }

  @media (max-width: ${bp.small}px) {
    max-height: calc(90vh - 80px);
    ${(props) =>
      props.size === "small"
        ? "width: 90%;"
        : "bottom: 0; border-radius: var(--mi-border-radius-focus) var(--mi-border-radius-focus) 0  0; width: 100%;"}
  }
`;

const closeBase = `
  background: transparent !important;
  background-color: transparent !important;
  border: none;
  filter: none !important;

  &:hover:not(:disabled),
  &:focus,
  &:focus-visible {
    background: transparent !important;
    background-color: transparent !important;
    filter: none !important;
  }
`;

const closeOnLight = css`
  color: var(--mi-black) !important;
  ${closeBase}

  svg {
    fill: var(--mi-black) !important;
  }
`;

const closeOnDark = css`
  color: var(--mi-off-white) !important;
  background: var(--mi-darken-background-color) !important;
  background-color: var(--mi-darken-background-color) !important;
  border: none;
  filter: none !important;

  svg {
    fill: var(--mi-off-white) !important;
  }

  &:hover:not(:disabled),
  &:focus,
  &:focus-visible {
    background: var(--mi-darken-x-background-color) !important;
    background-color: var(--mi-darken-x-background-color) !important;
    filter: none !important;
  }
`;

function slugifyTitle(title?: string | null) {
  return title ? title.toLowerCase().trim().replaceAll(" ", "-") : undefined;
}

function dialogIDFromTitle(title?: string | null) {
  return slugifyTitle(title);
}

function dialogLabelFromTitle(title?: string | null) {
  const slug = slugifyTitle(title);
  return slug ? `${slug}-label` : undefined;
}

export const Modal: React.FC<{
  open: boolean;
  children: React.ReactNode;
  title?: string | null;
  id?: string;
  ariaDescribedBy?: string | null;
  onClose: () => void;
  size?: ContentProps["size"];
  className?: string;
  contentClassName?: string;
  noPadding?: boolean;
  focusTrapOptions?: React.ComponentProps<typeof FocusTrap>["focusTrapOptions"];
}> = ({
  children,
  open,
  onClose,
  size,
  title,
  id,
  ariaDescribedBy,
  className,
  contentClassName,
  noPadding,
  focusTrapOptions,
}) => {
  const [container] = React.useState(() => {
    // This will be executed only on the initial render
    // https://reactjs.org/docs/hooks-reference.html#lazy-initial-state
    return document.createElement("div");
  });

  React.useEffect(() => {
    document.body.appendChild(container);
    return () => {
      document.body.removeChild(container);
    };
  }, [container]);

  const onCloseWrapper = React.useCallback(
    (
      e:
        | React.MouseEvent<HTMLButtonElement, MouseEvent>
        | React.MouseEvent<HTMLDivElement, MouseEvent>
        | React.KeyboardEvent<HTMLDivElement>
    ) => {
      e.stopPropagation();
      onClose();
    },
    [onClose]
  );

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "initial";
    }
  }, [open]);

  const childrenWrapperId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);

  if (!open) {
    return null;
  }

  const closeClass = title ? closeOnLight : closeOnDark;

  return ReactDOM.createPortal(
    <FocusTrap
      focusTrapOptions={{
        initialFocus: ".".concat(closeClass),
        // The dialog itself is always focusable (tabIndex={-1}), so the trap
        // never throws "must have at least one tabbable node" — even while a
        // modal's content is still empty (async/loading states).
        fallbackFocus: () => dialogRef.current ?? document.body,
        ...focusTrapOptions,
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        id={id || dialogIDFromTitle(title)}
        role="dialog"
        aria-describedby={ariaDescribedBy || undefined}
        aria-labelledby={dialogLabelFromTitle(title)}
        aria-modal="true"
        onKeyDown={(event) => {
          event.key === "Escape" && onCloseWrapper(event);
        }}
      >
        <Background onClick={onCloseWrapper} />
        <div className={wrapper} data-cy="modal">
          <Content size={size} contentTitle={title} className={className}>
            <SpaceBetweenDiv
              className={css`
                position: sticky;
                top: 0;
                margin-bottom: 0 !important;
                align-items: center;

                padding: 1rem;
                padding-bottom: 0.5rem !important;
                border-radius: var(--mi-border-radius-x)
                  var(--mi-border-radius-x) 0 0;
                background-color: inherit;
                ${title
                  ? "border-bottom: solid 1px var(--mi-tint-x-color);"
                  : ""}
                z-index: 12;

                ${title
                  ? "background-color: var(--mi-lighten-background-color) !important;"
                  : "justify-content: flex-end !important; background-color: transparent; padding-right: 0 !important;"}
                button {
                  ${!title ? "margin-bottom: 0.2rem;" : ""}
                }

                h2 {
                  flex: 85%;
                  max-width: 85%;
                  margin-bottom: 0 !important;
                }
              `}
            >
              {title && <h2 id={dialogLabelFromTitle(title)}>{title}</h2>}

              <Button
                className={closeClass}
                onlyIcon
                startIcon={<FaTimes />}
                onClick={onCloseWrapper}
                aria-label="close"
              />
            </SpaceBetweenDiv>
            <ChildrenWrapper className={contentClassName}>
              <ScrollArea id={childrenWrapperId} noPadding={noPadding}>
                {children}
              </ScrollArea>
            </ChildrenWrapper>
            <ScrollFadeOverlay
              scrollElementId={childrenWrapperId}
              position="bottom"
            />
          </Content>
        </div>
      </div>
    </FocusTrap>,
    container
  );
};

export default Modal;
